// Recovers from stale front-end assets after a deploy.
//
// We ship multiple releases per day. Each Vite build emits new
// content-hashed filenames and discards the previous ones, so a browser
// tab opened before a deploy still references chunks that no longer exist
// on the server. When such a tab lazy-loads a chunk (a dialog, a data
// grid, the next page component) the fetch 404s and the UI appears broken.
//
// Inertia navigations already self-heal via asset versioning (a version
// mismatch triggers a full reload). This module covers the remaining gap:
// in-page dynamic imports that fail outside of a navigation. We surface a
// short banner explaining why the page is refreshing, then reload to pull
// the latest assets.

const BANNER_ID = 'app-updating-banner';
const RELOAD_GUARD_KEY = 'app:reloaded-for-update';
const RELOAD_DELAY_MS = 1200;
// How long after a reload attempt we refuse to reload again. Prevents a
// genuinely-missing chunk (e.g. a broken deploy, not just a stale tab) from
// looping the page, while still letting a later deploy in a long-lived tab
// recover once the window lapses.
const RETRY_WINDOW_MS = 30000;
// On the suppressed path (reload refused) we still flash the banner, then
// remove it so a usable page isn't left under a permanent "updating…" bar.
const BANNER_DISMISS_MS = 5000;
const BANNER_MESSAGE = 'A new version is available — updating…';

let reloadScheduled = false;
let suppressionNoticeShown = false;

function showUpdatingBanner(): void {
  if (document.getElementById(BANNER_ID)) {
    return;
  }

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.setAttribute('role', 'status');
  banner.setAttribute('aria-live', 'polite');
  banner.textContent = BANNER_MESSAGE;

  // Inline styles intentionally: the failure we are recovering from is a
  // missing chunk, so we must not depend on any lazily-loaded CSS, MUI, or
  // React render to display this message.
  Object.assign(banner.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    zIndex: '2147483647',
    padding: '12px 16px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#FFFFFF',
    backgroundColor: '#082042',
    borderBottom: '3px solid #E49B3D',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  } satisfies Partial<CSSStyleDeclaration>);

  document.body.appendChild(banner);
}

function removeBanner(): void {
  document.getElementById(BANNER_ID)?.remove();
}

// sessionStorage can throw (Safari private mode historically, sandboxed
// iframes). Treat any failure as "no guard recorded" and fall back to the
// in-memory `reloadScheduled` flag, which still prevents a same-life loop.
function readLastReloadAt(): number | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function markReloadAttempt(at: number): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(at));
  } catch {
    // Storage unavailable — the in-memory flag is our only guard this life.
  }
}

/**
 * Show the updating banner and reload the page to fetch fresh assets.
 *
 * Guarded two ways: the in-memory `reloadScheduled` flag stops repeat reloads
 * within a single page life, and a timestamped `sessionStorage` entry stops a
 * tight reload loop across reloads. If a reload happened within the retry
 * window we only flash the banner (and auto-dismiss it) rather than reloading
 * again, so a genuinely-broken asset (a bad deploy, not just a stale tab)
 * can't loop the page or leave the advisor stuck under a permanent banner.
 *
 * The timestamp is deliberately NOT cleared on a successful load: the document
 * shell loads fine even when a lazy chunk is permanently missing, so clearing
 * on load would re-arm the loop on every reload. Instead the guard simply
 * expires after `RETRY_WINDOW_MS`, which both throttles a broken deploy and
 * still lets a genuine later deploy in a long-lived tab recover.
 */
export function recoverFromStaleAssets(): void {
  if (reloadScheduled) {
    return;
  }

  // A dynamic import can also fail simply because the user is offline. Reloading
  // then would swap the working SPA for the browser's "No Internet" page — worse
  // than doing nothing. Leave the app in place; a genuine stale-asset failure
  // will retry on the next interaction once connectivity returns.
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return;
  }

  const lastReloadAt = readLastReloadAt();
  const now = Date.now();

  if (lastReloadAt !== null && now - lastReloadAt < RETRY_WINDOW_MS) {
    // We reloaded recently and assets still fail — reloading again would loop.
    // Flash the banner once and let it dismiss, rather than stacking timers.
    if (!suppressionNoticeShown) {
      suppressionNoticeShown = true;
      showUpdatingBanner();
      window.setTimeout(removeBanner, BANNER_DISMISS_MS);
    }
    return;
  }

  reloadScheduled = true;
  markReloadAttempt(now);
  showUpdatingBanner();
  window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
}

/**
 * Registers the global listener that recovers from stale assets. Vite
 * dispatches `vite:preloadError` when a dynamically imported chunk fails to
 * load.
 *
 * This overlaps intentionally with the try/catch around Inertia's `resolve`
 * in App.tsx — a failed page chunk both throws into that catch and fires this
 * event. `recoverFromStaleAssets` is idempotent (the `reloadScheduled` guard),
 * so the two paths compose without double-reloading.
 */
export function registerStaleAssetRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Prevent Vite's default unhandled rejection so the only outcome the
    // advisor sees is our banner + reload, not a console/Sentry error spike.
    event.preventDefault();
    recoverFromStaleAssets();
  });
}
